import { FC, useCallback, useMemo } from 'react';
import {
    Badge,
    Button,
    ButtonLink,
    ButtonSizes,
    ButtonStyles,
    EntityChip,
    Icons,
    Tooltip
} from '@silverfort/sf-components';
import { Module, VALUE_MAX } from './types';
import { bigNumberToText, downloadFile } from '../../../utils';
import moment from 'moment';

type LicenseModuleRowProps = Module & {
    shouldShowUsage?: boolean;
    shouldHideOnPremIcon?: boolean;
    usage?: number;
    quantityLabel?: string;
    shouldShowUsageOnDisabled?: boolean;
};

const LicenseModuleRow: FC<LicenseModuleRowProps> = ({
    label,
    value,
    cloud,
    onPrem,
    shouldShowUsage,
    shouldHideOnPremIcon,
    usage,
    learnMoreLink,
    getAccessLink,
    getCloudLink,
    quantityLabel,
    exportFilePath,
    shouldShowUsageOnDisabled = false
}) => {
    const isEnabled = useMemo(() => cloud?.enabled || onPrem?.enabled, [cloud, onPrem]);
    const isOnPremTrialEnabled = useMemo(() => {
        const now = new Date();
        return onPrem?.trial?.enabled && new Date(onPrem.trial?.expirationDate) > now;
    }, [onPrem]);

    const isCloudTrialEnabled = useMemo(() => {
        const now = new Date();
        return cloud?.trial?.enabled && new Date(cloud.trial?.expirationDate) > now;
    }, [cloud]);

    const isTrialEnabled = useMemo(
        () => isCloudTrialEnabled || isOnPremTrialEnabled,
        [isCloudTrialEnabled, isOnPremTrialEnabled]
    );

    const onDownloadClicked = useCallback(() => {
        if (!exportFilePath) return;
        try {
            downloadFile(exportFilePath);
        } catch (err) {
            console.error('an error occurred while trying to download export file.');
        }
    }, [exportFilePath]);

    const warningBadge = useMemo(() => {
        if (!(isEnabled || isTrialEnabled) || !usage || value === VALUE_MAX) return null;
        if (usage > value) {
            return (
                <Tooltip title="You've exceeded the limit of your current license usage">
                    <Badge
                        text="Exceeded limit"
                        icon="Warning"
                        color="negative"
                        customClassNames={{ background: 'bg-sf-negative-200' }}
                    />
                </Tooltip>
            );
        }
        if (usage === value) {
            return (
                <Tooltip title="You're currently using 100% of your license capacity">
                    <Badge text="Limit reached" icon="Warning" color="negative" />
                </Tooltip>
            );
        }
        if (usage / value >= 0.8) {
            const percentage = Math.round((usage / value) * 100);
            return (
                <Tooltip title={`You're currently using most of your license (${percentage})%`}>
                    <Badge text="Approached limit" icon="Warning" color="neutral" />
                </Tooltip>
            );
        }
        return <></>;
    }, [isEnabled, isTrialEnabled, usage, value]);

    const trialBadge = useMemo(() => {
        if (!isTrialEnabled) return <></>;

        const trialDate = onPrem?.trial?.enabled
            ? new Date(onPrem.trial.expirationDate)
            : cloud?.trial?.enabled
            ? new Date(cloud.trial.expirationDate)
            : null;
        if (!trialDate) return <></>;

        const badgeText = `Trial: Exp. ${moment(trialDate).format('MMM DD, YYYY')}`;

        return <Badge text={badgeText} icon="GaugeChart" color="secondary" />;
    }, [isTrialEnabled, onPrem, cloud]);

    const accessButton = useMemo(() => {
        if (!isEnabled && getAccessLink) {
            return (
                <Button
                    text="Get Access"
                    textSize="text-12"
                    buttonSize={ButtonSizes.Small}
                    onClick={() => window.open(getAccessLink, '_blank')}
                />
            );
        }
        if (onPrem?.enabled && cloud && !cloud.enabled && getCloudLink) {
            return (
                <Button
                    text="Get Cloud"
                    textSize="text-12"
                    buttonSize={ButtonSizes.Small}
                    onClick={() => window.open(getCloudLink, '_blank')}
                />
            );
        }
        return <></>;
    }, [isEnabled, getAccessLink, onPrem, cloud, getCloudLink]);

    const getValueUsageQuantityChips = () => {
        if ((isTrialEnabled && !isEnabled) || (!shouldShowUsage && value === VALUE_MAX)) return null;

        const valueText =
            value !== VALUE_MAX && quantityLabel
                ? `${bigNumberToText(value, false, 0)} ${quantityLabel}`
                : bigNumberToText(value, false, 0);

        const usageText = shouldShowUsage && `${bigNumberToText(usage, false, 0)} Used`;

        return (
            <>
                <span className="text-sf-gray-500 text-[8px]">•</span>
                <div className="flex group">
                    {valueText && (
                        <EntityChip
                            text={valueText}
                            className={usageText ? 'rounded-r-none group-hover:border-sf-gray-300' : ''}
                        />
                    )}
                    {usageText && (
                        <EntityChip
                            text={usageText}
                            className={valueText ? 'rounded-l-none border-l-0 group-hover:border-sf-gray-300' : ''}
                        />
                    )}
                </div>
            </>
        );
    };

    const getDisabledValueUsageQuantityChips = () => {
        if (shouldShowUsageOnDisabled)
            return (
                <>
                    <span className="text-sf-gray-500 text-[8px]">•</span>
                    <div className="flex group">
                        <EntityChip
                            text={quantityLabel ? `0 ${quantityLabel}` : '0'}
                            className="rounded-r-none group-hover:border-sf-gray-300"
                        />
                        <EntityChip
                            text="0 Used"
                            className="rounded-l-none border-l-0 group-hover:border-sf-gray-300"
                        />
                    </div>
                </>
            );
    };

    return (
        <div
            className={`flex justify-between h-20 ${
                isEnabled || isTrialEnabled ? '' : 'bg-[linear-gradient(266deg,_#E9F5FF_0%,_#FFF_16.23%)]'
            } px-6 items-center border rounded-lg`}>
            <div className="flex items-center gap-4">
                <div className="text-sf-gray-800 text-14 font-medium">{label}</div>
                {isEnabled || isTrialEnabled ? (
                    <div className="flex items-center gap-2">
                        {!shouldHideOnPremIcon && (onPrem?.enabled || isOnPremTrialEnabled) && (
                            <EntityChip
                                text="On-prem"
                                icon={isOnPremTrialEnabled ? Icons.GaugeChart : Icons.Server2Dots}
                            />
                        )}
                        {(cloud?.enabled || isCloudTrialEnabled) && (
                            <EntityChip
                                text="Cloud"
                                icon={isCloudTrialEnabled ? Icons.GaugeChart : Icons.ServerCloud}
                            />
                        )}
                        {getValueUsageQuantityChips()}
                    </div>
                ) : (
                    getDisabledValueUsageQuantityChips()
                )}
            </div>
            <div className="flex items-center gap-2">
                {trialBadge}
                {warningBadge}
                {learnMoreLink && (
                    <ButtonLink
                        text="Learn More"
                        underline
                        className="flex-shrink-0 text-13 font-normal text-gray-400"
                        onClick={() => window.open(learnMoreLink, '_blank')}
                    />
                )}
                {(isEnabled || isTrialEnabled) && shouldShowUsage && exportFilePath && (
                    <Button
                        iconLeft={Icons.Download}
                        buttonSize={ButtonSizes.Small}
                        buttonStyle={ButtonStyles.Primary}
                        onClick={onDownloadClicked}
                    />
                )}
                {accessButton}
            </div>
        </div>
    );
};

export default LicenseModuleRow;
